import { openDB as idbOpenDB } from 'idb';

const DB_NAME = 'photohunt-db';
const DB_VERSION = 1;

let dbPromise = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = idbOpenDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Hunts store — one record per Hunt (Numbers/Alphabet/Custom)
        if (!db.objectStoreNames.contains('hunts')) {
          db.createObjectStore('hunts', { keyPath: 'id' });
        }

        // Photos store — flat across all hunts, scoped by huntId
        if (!db.objectStoreNames.contains('photos')) {
          const photoStore = db.createObjectStore('photos', { keyPath: 'id' });
          photoStore.createIndex('huntId',   'huntId',   { unique: false });
          photoStore.createIndex('itemKey',  'itemKey',  { unique: false });
          photoStore.createIndex('location', 'location', { unique: false });
        }

        // Sync queue store
        if (!db.objectStoreNames.contains('syncQueue')) {
          db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
        }

        // Meta store (lastSyncedAt, activeHuntId, shareTokens, etc.)
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta', { keyPath: 'key' });
        }

        // Pending blobs — thumbnail images queued for upload while offline,
        // keyed by photo id. Needed because a compressed Blob can't survive
        // in the syncQueue payload across a page reload; flushQueue reads
        // from here to upload the real image once we're back online.
        if (!db.objectStoreNames.contains('pendingBlobs')) {
          db.createObjectStore('pendingBlobs', { keyPath: 'id' });
        }
      },
    });
  }
  return dbPromise;
}

// ── Hunts ───────────────────────────────────────────────────────────────────

export async function getAllHunts() {
  const db = await getDB();
  return db.getAll('hunts');
}

export async function upsertHunt(hunt) {
  const db = await getDB();
  await db.put('hunts', hunt);
}

export async function deleteHuntLocal(id) {
  const db = await getDB();
  await db.delete('hunts', id);
}

export async function clearAllHunts() {
  const db = await getDB();
  await db.clear('hunts');
}

// ── Photos ──────────────────────────────────────────────────────────────────

export async function getAllPhotos() {
  const db = await getDB();
  return db.getAll('photos');
}

export async function getPhotosByHunt(huntId) {
  const db = await getDB();
  return db.getAllFromIndex('photos', 'huntId', huntId);
}

export async function getPhotosByItemKey(itemKey) {
  const db = await getDB();
  return db.getAllFromIndex('photos', 'itemKey', itemKey);
}

export async function getPhotosByLocation(location) {
  const db = await getDB();
  return db.getAllFromIndex('photos', 'location', location);
}

export async function upsertPhoto(photo) {
  const db = await getDB();
  await db.put('photos', photo);
}

export async function deletePhotoLocal(id) {
  const db = await getDB();
  await db.delete('photos', id);
}

export async function clearAllPhotos() {
  const db = await getDB();
  await db.clear('photos');
}

// ── Sync queue ───────────────────────────────────────────────────────────────

export async function enqueueOp(op, payload) {
  const db = await getDB();
  await db.add('syncQueue', { op, payload, createdAt: new Date().toISOString() });
}

export async function getAllQueuedOps() {
  const db = await getDB();
  return db.getAll('syncQueue');
}

export async function clearQueuedOp(id) {
  const db = await getDB();
  await db.delete('syncQueue', id);
}

export async function clearAllQueuedOps() {
  const db = await getDB();
  await db.clear('syncQueue');
}

// ── Pending blobs ────────────────────────────────────────────────────────────

export async function putPendingBlob(id, blob) {
  const db = await getDB();
  await db.put('pendingBlobs', { id, blob });
}

export async function getPendingBlob(id) {
  const db = await getDB();
  const record = await db.get('pendingBlobs', id);
  return record ? record.blob : null;
}

export async function deletePendingBlob(id) {
  const db = await getDB();
  await db.delete('pendingBlobs', id);
}

// ── Meta ─────────────────────────────────────────────────────────────────────

export async function getMeta(key) {
  const db = await getDB();
  const record = await db.get('meta', key);
  return record ? record.value : null;
}

export async function setMeta(key, value) {
  const db = await getDB();
  await db.put('meta', { key, value });
}
